import React, { useState } from "react";
import { FAQ_DATA } from "../data/FAQ";
import Container from "../components/common/Container";
import MetaTags from "../components/common/MetaTags";
import { HiChevronDown, HiSearch, HiQuestionMarkCircle } from "react-icons/hi";
import { Link } from "react-router-dom";

const FAQAccordion = ({ item, index, isOpen, onToggle }) => {
  return (
    <div
      className={`group rounded-2xl border transition-all duration-300 overflow-hidden ${
        isOpen
          ? "bg-gradient-to-br from-primary/5 to-primary-green/5 border-primary/30 shadow-lg shadow-primary/5"
          : "bg-white/50 border-gray-200 hover:border-primary/20 hover:shadow-md"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-6 text-left"
      >
        <div className="flex items-center gap-4">
          <span
            className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition-all duration-300 ${
              isOpen
                ? "bg-gradient-to-r from-primary to-primary-green text-white"
                : "bg-primary/10 text-primary"
            }`}
          >
            {String(index + 1).padStart(2, "0")}
          </span>
          <h3
            className={`text-lg font-semibold transition-colors duration-300 ${
              isOpen ? "text-primary" : "text-secondary"
            }`}
          >
            {item.title}
          </h3>
        </div>
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
            isOpen
              ? "bg-primary text-white rotate-180"
              : "bg-gray-100 text-gray-500 group-hover:bg-primary/10 group-hover:text-primary"
          }`}
        >
          <HiChevronDown size={20} />
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 pb-6 pt-0">
          <div className="pl-12">
            <p
              className="text-gray-600 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: item.details }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [openIndex, setOpenIndex] = useState(0);

  const filteredFAQ = FAQ_DATA.filter(
    (item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.details.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = [
    { name: "Getting Started", count: 3, icon: "rocket" },
    { name: "Funding", count: 5, icon: "coin" },
    { name: "Projects", count: 4, icon: "folder" },
    { name: "Security", count: 2, icon: "shield" },
  ];

  return (
    <div className="w-full flex flex-col items-center min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <MetaTags
        title="FAQ"
        description={`NFT- Fundraising By FunDeSci An innovative approach for decentralized crowdfunding of scientific projects`}
        link="/FAQ"
      />

      {/* Hero Section */}
      <section className="w-full relative overflow-hidden bg-gradient-to-br from-secondary via-[#2a1f52] to-[#1a1235] pt-40 pb-32">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-primary-green/15 rounded-full blur-[120px]" />
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <Container>
          <div className="relative z-10 flex flex-col items-center text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <HiQuestionMarkCircle className="text-primary-green" size={18} />
              <span className="text-white/90 text-sm font-medium">
                Help Center
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl abel-font font-bold text-white tracking-wide mb-6">
              Frequently Asked{" "}
              <span className="bg-gradient-to-r from-primary via-[#a855f7] to-primary-green bg-clip-text text-transparent">
                Questions
              </span>
            </h1>

            <p className="text-white/70 text-lg max-w-2xl mb-10">
              Find answers to common questions about FunDeSci, NFT fundraising,
              and how to get started with scientific research funding.
            </p>

            {/* Search Box */}
            <div className="w-full max-w-2xl relative">
              <div className="absolute inset-y-0 left-5 flex items-center pointer-events-none">
                <HiSearch className="text-gray-400" size={22} />
              </div>
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-14 pr-6 py-5 rounded-2xl bg-white/95 backdrop-blur-sm border-2 border-transparent focus:border-primary focus:outline-none text-secondary placeholder-gray-400 shadow-2xl shadow-black/20 transition-all duration-300"
              />
            </div>
          </div>
        </Container>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full"
          >
            <path
              d="M0 120L60 105C120 90 240 60 360 55C480 50 600 70 720 75C840 80 960 70 1080 60C1200 50 1320 40 1380 35L1440 30V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              className="fill-gray-50"
            />
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <Container>
        <div className="w-full py-16 lg:py-24">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Quick Links */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-32">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
                  Quick Links
                </h3>
                <div className="space-y-2">
                  {categories.map((cat, idx) => (
                    <button
                      key={idx}
                      className="w-full flex items-center justify-between p-4 rounded-xl bg-white/80 backdrop-blur-sm border border-white/20 hover:border-primary/30 hover:shadow-md transition-all duration-300 group"
                    >
                      <span className="text-secondary font-medium group-hover:text-primary transition-colors">
                        {cat.name}
                      </span>
                      <span className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                        {cat.count}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Contact Card */}
                <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-primary to-[#7e7cff] text-white">
                  <h4 className="font-semibold text-lg mb-2">
                    Still have questions?
                  </h4>
                  <p className="text-white/80 text-sm mb-4">
                    Can't find the answer you're looking for? Our team is here
                    to help.
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-primary font-medium hover:bg-white/90 transition-all duration-300"
                  >
                    Contact Support
                  </Link>
                </div>
              </div>
            </aside>

            {/* FAQ Accordion List */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-secondary">
                  {searchQuery ? "Search Results" : "All Questions"}
                </h2>
                <span className="px-4 py-2 rounded-full bg-gray-100 text-gray-600 text-sm font-medium">
                  {filteredFAQ.length}{" "}
                  {filteredFAQ.length === 1 ? "question" : "questions"}
                </span>
              </div>

              {filteredFAQ.length > 0 ? (
                <div className="space-y-4">
                  {filteredFAQ.map((item, index) => (
                    <FAQAccordion
                      key={index}
                      item={item}
                      index={index}
                      isOpen={openIndex === index}
                      onToggle={() =>
                        setOpenIndex(openIndex === index ? -1 : index)
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 px-8 rounded-2xl bg-gray-50 border border-gray-100">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                    <HiSearch className="text-primary" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-secondary mb-2">
                    No results found
                  </h3>
                  <p className="text-gray-500 text-center max-w-md">
                    We couldn't find any questions matching "{searchQuery}". Try
                    a different search term.
                  </p>
                  <button
                    onClick={() => setSearchQuery("")}
                    className="mt-6 px-6 py-3 rounded-xl bg-primary text-white font-medium hover:bg-primary/90 transition-all duration-300"
                  >
                    Clear Search
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default FAQ;
